"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { cn, formatCentsToCurrency } from "@mot-cards/ui";
import { formatMaxBucks } from "@mot-cards/types";
import { getCurrentUser, signout } from "@/lib/auth";
import { useLiveStatus } from "@/lib/use-live-status";
import { useMarketplaceAvailable } from "@/lib/marketplace/use-marketplace-available";
import { useWalletOptional } from "@/providers/wallet-provider";
import { maskBalance, useBalanceHidden } from "@/hooks/useBalanceHidden";
import { BalanceVisibilityToggle } from "@/components/wallet/balance-visibility-toggle";

// The logo is the way home, so there is no Home tab.
const NAV_ITEMS = [
  { label: "Packs", href: "/packs" },
  { label: "Live", href: "/live" },
  // Shown only once the marketplace API answers; it 404s while the feature is off.
  { label: "Marketplace", href: "/marketplace" },
  { label: "Collection", href: "/collection", memberOnly: true },
  /*
   * MY VAULT IS HIDDEN FOR LAUNCH — Doc, Aug 21: the 3D world is not shipping
   * yet.
   *
   * Commented rather than deleted, and the note kept with it, because this is
   * a launch decision and not a change of mind about the nav. When the vault
   * ships, uncommenting this line is the whole job.
   *
   * It sat next to Collection on purpose: the collection is what you own, the
   * vault is where you put it. And it was NOT member-only, unlike its
   * neighbours — Collection and Wallet have nothing to say to a signed-out
   * visitor and would only 401, whereas the vault arranges itself locally and
   * says plainly that signing in is what keeps it, which made it the one thing
   * here worth showing someone before they had an account.
   *
   * THE ROUTE IS STILL LIVE. This hides the entrance, not the room —
   * /vault and /vault/explore still render for anyone with the URL.
   */
  // { label: "My Vault", href: "/vault" },
] as const;

type NavLink = { label: string; href: string };

function isNavActive(pathname: string, href: string) {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

function IconMenu({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
    </svg>
  );
}

function IconClose({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
    </svg>
  );
}

function IconWallet({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M3 7h15a3 3 0 0 1 3 3v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
      <path d="M17 12h4v3h-4a1.5 1.5 0 0 1 0-3z" />
    </svg>
  );
}

function IconInstagram({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" />
    </svg>
  );
}

function IconX({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.64 7.58H.47l8.6-9.83L0 1.15h7.59l5.25 6.93zm-1.29 19.5h2.04L6.49 3.24H4.3z" />
    </svg>
  );
}

function IconTikTok({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  );
}

function Logo() {
  return (
    <Link
      href="/"
      className="group -ml-2 flex items-center rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-mot-teal sm:-ml-3"
      aria-label="MAX Options Trading Cards — home"
    >
      <Image
        src="/hero/max-logo.webp"
        alt="MAX Options Trading Cards"
        width={1536}
        height={924}
        priority
        className="h-14 w-auto object-contain transition-transform group-hover:scale-[1.03] sm:h-16"
      />
    </Link>
  );
}

function MobileMenuLink({
  item,
  pathname,
  onNavigate,
}: {
  item: NavLink;
  pathname: string;
  onNavigate: () => void;
}) {
  const active = isNavActive(pathname, item.href);
  return (
    <Link
      href={item.href}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={cn(
        "block w-full rounded-md px-3 py-2.5 text-left text-sm font-medium",
        active ? "bg-dark-card text-mot-text" : "text-mot-grey-light",
      )}
    >
      {item.label}
    </Link>
  );
}

function IconChevronDown({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** First letter of the email's local part: all the identity the client caches. */
function accountInitial(email: string | undefined) {
  const letter = email?.trim().charAt(0).toUpperCase();
  return letter && /[A-Z0-9]/.test(letter) ? letter : "?";
}

const MENU_ITEM_CLASS =
  "flex w-full items-center rounded-md px-3 py-2.5 text-left text-sm font-semibold text-mot-text transition-colors hover:bg-white/[0.06] hover:text-mot-teal focus-visible:bg-white/[0.06] focus-visible:text-mot-teal focus-visible:outline-none";

function AccountMenu({
  email,
  items,
  onSignOut,
}: {
  email: string | undefined;
  items: ReadonlyArray<NavLink>;
  onSignOut: () => void;
}) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Which item takes focus when the menu opens: first for click/Enter/ArrowDown, last for ArrowUp.
  const focusOnOpen = useRef<"first" | "last">("first");

  const menuItems = () =>
    Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const list = menuItems();
    (focusOnOpen.current === "last" ? list.at(-1) : list[0])?.focus();

    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const onButtonKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    focusOnOpen.current = event.key === "ArrowUp" ? "last" : "first";
    setOpen(true);
  };

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    const list = menuItems();
    const index = list.indexOf(document.activeElement as HTMLElement);
    const focusAt = (next: number) => list[(next + list.length) % list.length]?.focus();
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        close(true);
        break;
      case "Tab":
        // Let focus move on naturally; the menu just stops being open behind it.
        setOpen(false);
        break;
      case "ArrowDown":
        event.preventDefault();
        focusAt(index + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        focusAt(index - 1);
        break;
      case "Home":
        event.preventDefault();
        focusAt(0);
        break;
      case "End":
        event.preventDefault();
        focusAt(list.length - 1);
        break;
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          focusOnOpen.current = "first";
          setOpen((o) => !o);
        }}
        onKeyDown={onButtonKeyDown}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? "account-menu" : undefined}
        aria-label="Account menu"
        className={cn(
          "group flex items-center gap-1.5 rounded-full border p-1 pr-2 transition-[border-color,transform] duration-150 active:scale-[0.97] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mot-teal motion-reduce:active:scale-100",
          open ? "border-mot-teal/60" : "border-dark-border hover:border-mot-teal/60",
        )}
      >
        <span
          aria-hidden="true"
          className="grid size-8 place-items-center rounded-full bg-mot-teal/15 text-sm font-extrabold text-mot-teal"
        >
          {accountInitial(email)}
        </span>
        <IconChevronDown
          className={cn(
            "size-4 text-mot-grey-light transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] group-hover:text-mot-text motion-reduce:transition-none",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <div
          ref={menuRef}
          id="account-menu"
          role="menu"
          aria-label="Account"
          onKeyDown={onMenuKeyDown}
          className="absolute right-0 top-full z-50 mt-2 w-60 origin-top-right rounded-xl border border-dark-border bg-[#0b1013]/95 p-1.5 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.7)] backdrop-blur-xl transition-[opacity,transform] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] starting:-translate-y-1 starting:scale-[0.97] starting:opacity-0 motion-reduce:starting:translate-y-0 motion-reduce:starting:scale-100"
        >
          {items.map((item) => {
            const active = isNavActive(pathname, item.href);
            return (
              <Link
                key={item.label}
                href={item.href}
                role="menuitem"
                tabIndex={-1}
                onClick={() => close(false)}
                aria-current={active ? "page" : undefined}
                className={cn(MENU_ITEM_CLASS, active && "text-mot-teal")}
              >
                {item.label}
              </Link>
            );
          })}
          <div role="separator" className="my-1.5 h-px bg-dark-border" />
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            onClick={() => {
              close(false);
              onSignOut();
            }}
            className={cn(MENU_ITEM_CLASS, "text-mot-grey-light")}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function TopNav({ minimized = false }: { minimized?: boolean }) {
  const pathname = usePathname();
  const router = useRouter();
  const wallet = useWalletOptional();
  const { hidden: balanceHidden } = useBalanceHidden();
  const { isLive, hasSchedule } = useLiveStatus();
  const marketplaceAvailable = useMarketplaceAvailable();
  const [mounted, setMounted] = useState(false);
  // Set by middleware from the user agent: true exactly when / is the pack page.
  const [phoneHome, setPhoneHome] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const authed = email !== null;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
    setPhoneHome(/(?:^|;\s*)motc_device=mobile(?:;|$)/.test(document.cookie));
    setEmail(getCurrentUser()?.email ?? null);
  }, []);

  // Below lg the hamburger carries Help, so the floating support launcher
  // stands down (globals.css). Pages without this nav keep the launcher.
  useEffect(() => {
    if (minimized) return;
    const root = document.documentElement;
    root.setAttribute("data-mot-nav-help", "");
    return () => root.removeAttribute("data-mot-nav-help");
  }, [minimized]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (pathname === "/") return;

    const timer = window.setTimeout(() => {
      router.prefetch("/");
    }, 0);

    return () => window.clearTimeout(timer);
  }, [pathname, router]);

  const handleSignOut = useCallback(async () => {
    try {
      await signout();
      window.location.href = "/";
    } catch {
      window.alert("Sign-out failed. Please check your connection and try again.");
    }
  }, []);

  const accountItems: ReadonlyArray<NavLink> = [
    { label: "Profile", href: "/profile" },
    { label: "Wallet", href: "/wallet" },
  ];
  const navItems: ReadonlyArray<NavLink> = NAV_ITEMS.filter((item) => {
    // Nothing scheduled and nobody live: the Live tab would only lead to an
    // empty page, so it is hidden until there is a show to point at. The
    // /live route itself stays reachable by URL.
    if (item.href === "/live" && !hasSchedule) return false;
    if (item.href === "/marketplace" && !marketplaceAvailable) return false;
    return !("memberOnly" in item && item.memberOnly) || (mounted && authed);
  });
  const maxBucksCents = wallet?.maxBucksCents ?? 0;
  const balanceLabel =
    wallet?.loading ? "…" : formatCentsToCurrency(wallet?.balanceCents ?? 0);

  if (minimized) {
    return (
      <header className="pointer-events-none fixed inset-x-0 top-0 z-40 flex justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))]">
        <div className="pointer-events-auto opacity-40 transition-opacity hover:opacity-100">
          <Logo />
        </div>
      </header>
    );
  }

  // viewport-fit=cover (root layout, #1020) lets the page run under the iPhone
  // status bar; the nav pads itself down past it.
  return (
    <header
      id="site-navigation"
      tabIndex={-1}
      className="sticky top-0 z-40 border-b border-white/[0.08] bg-[#05080a]/85 pt-[env(safe-area-inset-top)] backdrop-blur-xl"
    >
      <div className="flex h-20 w-full items-center justify-between px-6 sm:px-8 lg:px-10">
        <Logo />

        <div className="flex items-center gap-2 lg:gap-5">
          <nav className="hidden items-center gap-6 lg:flex xl:gap-8" aria-label="Primary">
            {navItems.map((item) => {
              const active = isNavActive(pathname, item.href);
              return (
                <Link
                  key={item.label}
                  href={item.href}
                  className={cn(
                    "text-sm font-bold uppercase tracking-[0.12em] transition-colors focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-mot-teal",
                    active ? "text-mot-teal" : "text-mot-text hover:text-mot-teal",
                  )}
                  aria-current={active ? "page" : undefined}
                >
                  {item.label}
                  {item.href === "/live" && isLive ? (
                    <>
                      <span
                        aria-hidden="true"
                        className="ml-1.5 inline-block size-1.5 rounded-full bg-mot-teal align-middle motion-safe:animate-pulse"
                      />
                      <span className="sr-only"> (live now)</span>
                    </>
                  ) : null}
                </Link>
              );
            })}
          </nav>

          <div className="hidden h-6 w-px bg-dark-border lg:block" aria-hidden="true" />

          {mounted && authed && (
            <div className="flex items-center rounded-full border border-dark-border bg-dark-card pr-1 transition-colors hover:border-mot-teal/60">
              <Link
                href="/wallet"
                className="flex items-center gap-2 rounded-full py-2 pl-3.5 pr-1 text-sm font-semibold text-mot-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mot-teal"
              >
                <IconWallet className="size-4 text-mot-teal" />
                <span className="tabular-nums">{maskBalance(balanceLabel, balanceHidden)}</span>
                <span className="hidden text-[11px] font-medium uppercase tracking-widest text-mot-grey-light sm:inline">
                  Balance
                </span>
                {maxBucksCents > 0 && !wallet?.loading ? (
                  <span
                    className="rounded-full border border-mot-teal/40 bg-mot-teal/10 px-2 py-0.5 text-[11px] font-bold tabular-nums text-mot-teal"
                    title="Max Bucks can only be used to rip packs"
                  >
                    {maskBalance(formatMaxBucks(maxBucksCents), balanceHidden)}
                  </span>
                ) : null}
              </Link>
              <BalanceVisibilityToggle />
            </div>
          )}

          {mounted && authed ? (
            <div className="hidden lg:block">
              <AccountMenu email={email ?? undefined} items={accountItems} onSignOut={handleSignOut} />
            </div>
          ) : mounted ? (
            <Link
              href="/login"
              className="hidden rounded-full border-2 border-mot-text/80 px-5 py-2 text-sm font-bold uppercase tracking-[0.12em] text-mot-text transition-colors hover:border-mot-teal hover:text-mot-teal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mot-teal sm:inline-flex"
            >
              Member Login
            </Link>
          ) : null}

          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="grid size-9 place-items-center rounded-md border border-dark-border text-mot-text lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <IconClose className="size-5" /> : <IconMenu className="size-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-white/[0.08] bg-[#05080a]/95 px-4 py-3 lg:hidden" aria-label="Mobile">
          <ul className="flex flex-col gap-1">
            {/* On a phone the pack page IS the homepage (middleware rewrite),
                so a Packs entry would be a second door to the same room. Keyed
                on the same device signal as the rewrite, not on viewport width:
                a narrow desktop window still gets the real homepage and keeps Packs. */}
            {navItems.filter((item) => !(phoneHome && item.href === "/packs")).map((item) => (
              <li key={item.label}>
                <MobileMenuLink item={item} pathname={pathname} onNavigate={() => setOpen(false)} />
              </li>
            ))}
            {mounted && (
              <li role="separator" aria-hidden="true" className="mx-3 my-1.5 h-px bg-dark-border" />
            )}
            {mounted && authed && accountItems.map((item) => (
              <li key={item.label}>
                <MobileMenuLink item={item} pathname={pathname} onNavigate={() => setOpen(false)} />
              </li>
            ))}
            {mounted && (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    // The widget is lazy; it reads this on mount if the event lands first.
                    try {
                      localStorage.setItem("mot_support_chat_open", "1");
                    } catch {
                      /* */
                    }
                    window.dispatchEvent(new CustomEvent("mot-open-support"));
                  }}
                  className="w-full rounded-md px-3 py-2.5 text-left text-sm font-medium text-mot-grey-light"
                >
                  Help
                </button>
              </li>
            )}
            {mounted && authed && (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    handleSignOut();
                    setOpen(false);
                  }}
                  className="w-full rounded-md px-3 py-2.5 text-left text-sm font-medium text-mot-grey-light"
                >
                  Sign out
                </button>
              </li>
            )}
            {mounted && !authed && (
              <li>
                <MobileMenuLink
                  item={{ label: "Member Login", href: "/login" }}
                  pathname={pathname}
                  onNavigate={() => setOpen(false)}
                />
              </li>
            )}
          </ul>
        </nav>
      )}
    </header>
  );
}

const FOOTER_PRODUCT = [
  { href: "/packs", label: "Packs" },
  { href: "/collection", label: "Collection" },
  { href: "/wallet", label: "Wallet" },
  { href: "/profile", label: "Profile" },
] as const;

const FOOTER_COMPANY = [
  { href: "/blog", label: "Blog" },
  { href: "/live", label: "Live" },
  { href: "/faq", label: "FAQ" },
  { href: "/help", label: "Support" },
] as const;

const FOOTER_LEGAL = [
  { href: "/terms", label: "Terms" },
  { href: "/privacy", label: "Privacy" },
  { href: "/accessibility", label: "Accessibility" },
] as const;

const FOOTER_SOCIAL = [
  { href: "https://www.instagram.com/MAXOPTIONSTRADINGCARDS/", label: "Instagram", Icon: IconInstagram },
  { href: "https://x.com/Maxoptionscards", label: "X", Icon: IconX },
  { href: "https://www.tiktok.com/@maxoptionstradingcards", label: "TikTok", Icon: IconTikTok },
] as const;

export function SiteFooter() {
  const year = new Date().getFullYear();
  const { hasSchedule } = useLiveStatus();
  // Same rule as the top nav: no shows and nobody live means the Live link
  // would only lead to an empty page.
  const companyLinks = FOOTER_COMPANY.filter(
    (link) => link.href !== "/live" || hasSchedule,
  );

  return (
    <footer className="border-t border-dark-border bg-mot-canvas">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
          <div className="max-w-xs">
            <Link
              href="/"
              className="inline-block rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-mot-teal"
              aria-label="MAX Options Trading Cards — home"
            >
              <Image
                src="/hero/max-logo.webp"
                alt="MAX Options Trading Cards"
                width={1536}
                height={924}
                className="h-16 w-auto object-contain"
              />
            </Link>
            <p className="mt-3 text-sm leading-relaxed text-mot-grey-light">
              Real graded cards held in PSA Vault custody. Rip packs, pull grails, keep, or use instant sell back:
              90% CL Value instant buyback on every card.
            </p>
            <ul className="mt-4 flex items-center gap-2">
              {FOOTER_SOCIAL.map(({ href, label, Icon }) => (
                <li key={href}>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-dark-border text-mot-grey-light transition-colors hover:text-mot-teal focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mot-teal"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-8 text-sm sm:grid-cols-3">
            <FooterCol title="Product" links={FOOTER_PRODUCT} />
            <FooterCol title="Company" links={companyLinks} />
            <FooterCol title="Legal" links={FOOTER_LEGAL} />
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-2 border-t border-dark-border pt-6 text-xs text-mot-grey-light sm:flex-row sm:items-center sm:justify-between">
          <p>© {year} MOT Cards — Max Options Trading. All rights reserved.</p>
          <p>Play responsibly. Must be 18+ to rip.</p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({
  title,
  links,
}: {
  title: string;
  links: readonly { href: string; label: string }[];
}) {
  return (
    <div>
      <h2 className="text-xs font-extrabold uppercase tracking-widest text-mot-text/70">
        {title}
      </h2>
      <ul className="mt-3 flex flex-col gap-2">
        {links.map(({ href, label }) => (
          <li key={href}>
            <Link href={href} className="text-mot-grey-light transition-colors hover:text-mot-teal">
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
